import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import { QrCode, Download, AddToQueue } from '@mui/icons-material';

export type EntityType = 'work_order' | 'material_lot' | 'work_center' | 'location' | 'employee';

export interface QREntity {
  id: string;
  type: EntityType;
  code: string;
  name: string;
  metadata?: Record<string, string>;
}

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'work_order', label: 'Work Order' },
  { value: 'material_lot', label: 'Material Lot' },
  { value: 'work_center', label: 'Work Center' },
  { value: 'location', label: 'Location' },
  { value: 'employee', label: 'Employee' },
];

// Mock entity data by type
const MOCK_ENTITIES: Record<EntityType, QREntity[]> = {
  work_order: [
    { id: '1', type: 'work_order', code: 'WO-2024-001', name: 'Assembly A — Op 010', metadata: { workCenter: 'WC-001', dueDate: '2024-01-17' } },
    { id: '2', type: 'work_order', code: 'WO-2024-002', name: 'Component B — Final Inspection', metadata: { workCenter: 'WC-003', dueDate: '2024-01-18' } },
    { id: '3', type: 'work_order', code: 'WO-2024-003', name: 'Module C — Packaging', metadata: { workCenter: 'WC-001', dueDate: '2024-01-20' } },
  ],
  material_lot: [
    { id: '4', type: 'material_lot', code: 'LOT-RM001-001', name: 'Steel Rod 12mm — Lot 001', metadata: { qty: '500 pcs', expiry: '2025-12-31' } },
    { id: '5', type: 'material_lot', code: 'LOT-RM002-001', name: 'Bolts M8x30 — Lot 001', metadata: { qty: '2000 pcs' } },
  ],
  work_center: [
    { id: '6', type: 'work_center', code: 'WC-001', name: 'Assembly Line 1', metadata: { dept: 'Assembly', capacity: '8 hrs/day' } },
    { id: '7', type: 'work_center', code: 'WC-002', name: 'CNC Milling', metadata: { dept: 'Machining', capacity: '24 hrs/day' } },
  ],
  location: [
    { id: '8', type: 'location', code: 'AREA-A', name: 'Area A — Assembly', metadata: { plant: 'Main Plant' } },
    { id: '9', type: 'location', code: 'LINE-A1', name: 'Assembly Line 1', metadata: { area: 'Area A' } },
  ],
  employee: [
    { id: '10', type: 'employee', code: 'EMP-001', name: 'John Smith', metadata: { dept: 'Assembly', role: 'Operator' } },
    { id: '11', type: 'employee', code: 'EMP-002', name: 'Jane Doe', metadata: { dept: 'Assembly', role: 'Operator' } },
  ],
};

interface QRGeneratorProps {
  onAddToPrintQueue: (entity: QREntity) => void;
}

const QRGenerator: React.FC<QRGeneratorProps> = ({ onAddToPrintQueue }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [entityType, setEntityType] = useState<EntityType>('work_order');
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [qrGenerated, setQrGenerated] = useState(false);
  const [addedMessage, setAddedMessage] = useState(false);

  const entities = MOCK_ENTITIES[entityType] || [];
  const filteredEntities = entities.filter(
    (e) =>
      !searchText ||
      e.code.toLowerCase().includes(searchText.toLowerCase()) ||
      e.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const selectedEntity = entities.find((e) => e.id === selectedEntityId) || null;

  // Generate QR code on canvas using a simple approach
  // We draw a placeholder since qrcode package requires Node.js canvas or browser's canvas API
  useEffect(() => {
    if (!selectedEntity || !canvasRef.current) {
      setQrGenerated(false);
      return;
    }
    generateQRCanvas(selectedEntity);
  }, [selectedEntity]);

  const generateQRCanvas = (entity: QREntity) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = 200;
    canvas.width = size;
    canvas.height = size;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    // Draw a simplified QR-like pattern using the entity code as seed
    ctx.fillStyle = '#000000';

    // Finder patterns (corners)
    const drawFinder = (x: number, y: number) => {
      ctx.fillRect(x, y, 21, 21);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x + 3, y + 3, 15, 15);
      ctx.fillStyle = '#000000';
      ctx.fillRect(x + 6, y + 6, 9, 9);
    };
    drawFinder(10, 10);
    drawFinder(size - 31, 10);
    drawFinder(10, size - 31);

    // Data modules (pseudo-random based on code)
    const code = entity.code;
    let hash = 0;
    for (let i = 0; i < code.length; i++) {
      hash = code.charCodeAt(i) + ((hash << 5) - hash);
    }

    const moduleSize = 5;
    const offset = 40;
    const cols = Math.floor((size - offset * 2) / moduleSize);
    const rows = Math.floor((size - offset * 2) / moduleSize);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const seed = Math.sin(hash + row * cols + col) * 10000;
        if (seed - Math.floor(seed) > 0.45) {
          ctx.fillRect(
            offset + col * moduleSize,
            offset + row * moduleSize,
            moduleSize - 1,
            moduleSize - 1
          );
        }
      }
    }

    // Label
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(entity.code, size / 2, size - 4);

    setQrGenerated(true);
  };

  const handleDownloadPNG = () => {
    if (!canvasRef.current || !selectedEntity) return;
    const link = document.createElement('a');
    link.download = `QR_${selectedEntity.code}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handleAddToQueue = () => {
    if (!selectedEntity) return;
    onAddToPrintQueue(selectedEntity);
    setAddedMessage(true);
    setTimeout(() => setAddedMessage(false), 2500);
  };

  const handleTypeChange = (type: EntityType) => {
    setEntityType(type);
    setSelectedEntityId('');
    setSearchText('');
    setQrGenerated(false);
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            QR Code Generator
          </Typography>

          <Box display="flex" gap={3} flexWrap="wrap">
            {/* Left: Controls */}
            <Box flex={1} minWidth={280}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityType}
                  label="Entity Type"
                  onChange={(e) => handleTypeChange(e.target.value as EntityType)}
                >
                  {ENTITY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder={`Search ${entityType.replace('_', ' ')}s...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Select Entity *</InputLabel>
                <Select
                  value={selectedEntityId}
                  label="Select Entity *"
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {filteredEntities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id}>
                      {entity.code} — {entity.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedEntity && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Entity Details
                  </Typography>
                  <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                    <Chip label={selectedEntity.type.replace('_', ' ').toUpperCase()} size="small" color="primary" />
                    <Chip label={selectedEntity.code} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2">{selectedEntity.name}</Typography>
                  {selectedEntity.metadata && (
                    <Box mt={1}>
                      {Object.entries(selectedEntity.metadata).map(([key, value]) => (
                        <Typography key={key} variant="caption" display="block" color="textSecondary">
                          {key}: {value}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {addedMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Added to print queue!
                </Alert>
              )}

              <Box display="flex" gap={1} flexDirection="column">
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownloadPNG}
                  disabled={!qrGenerated}
                  fullWidth
                >
                  Download PNG
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddToQueue />}
                  onClick={handleAddToQueue}
                  disabled={!selectedEntity}
                  fullWidth
                >
                  Add to Print Queue
                </Button>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Right: QR Preview */}
            <Box display="flex" flexDirection="column" alignItems="center" minWidth={240}>
              <Typography variant="subtitle2" gutterBottom>
                QR Code Preview
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 240,
                  minWidth: 220,
                  bgcolor: selectedEntity ? '#fff' : 'grey.50',
                }}
              >
                {!selectedEntity ? (
                  <Box textAlign="center">
                    <QrCode sx={{ fontSize: 80, color: 'action.disabled' }} />
                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                      Select an entity to generate QR code
                    </Typography>
                  </Box>
                ) : (
                  <Box textAlign="center">
                    <canvas
                      ref={canvasRef}
                      style={{
                        border: '1px solid #ddd',
                        display: 'block',
                        maxWidth: '100%',
                      }}
                    />
                    {selectedEntity && (
                      <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                        {selectedEntity.code}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QRGenerator;
export { MOCK_ENTITIES };
